import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import cn from "classnames";
import styles from "./StudentDetail.module.sass";
import { Link } from "react-router-dom";
import Icon from "../../components/Icon";

// Screens
import NameAndDescription from "./NameAndDescription";
import ContractAndFiles from "./ContractAndFiles";
import MemosAndNotes from "./MemosAndNotes";
import Tutor from "./Tutor";
import ContractAndConvention from "./ContractAndFiles";


// Components  
import Card from "../../components/Card";
import Dropdown from "../../components/Dropdown";
import Actions from "../../components/Actions";

// Services
import { getCompanyById, subscribeToCompanyContracts, unsubscribeFromContracts, sendCompanyCredentials } from "../../services/company.service";
import { countActiveCompanyContracts } from "../../services/contract.service";
import { showSuccess, showError } from "../../services/toast.service";

const navigation = ["Coordonnées", "Maîtres d'apprentissage","Contrat / Convention", "Historique"];

const CompanyDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [company, setCompany] = useState(location.state?.company || null);
  const [loading, setLoading] = useState(!location.state?.company);
  const [error, setError] = useState(null);
  const [activeContractsCount, setActiveContractsCount] = useState(0);
  const [updatingAction, setUpdatingAction] = useState(false);

  // Fonction pour renvoyer les identifiants
  const handleSendCredentials = async () => {
    if (!company) return;
    
    try {
      setUpdatingAction(true);
      
      await sendCompanyCredentials(company.id);
      
      showSuccess('Email de réinitialisation envoyé avec succès au directeur de l\'entreprise!');
    } catch (error) {
      console.error('Erreur lors du renvoi des identifiants:', error);
      showError(error.message || 'Erreur lors du renvoi des identifiants');
    } finally {
      setUpdatingAction(false);
    }
  };

  // Récupérer l'entreprise depuis l'API si elle n'est pas déjà disponible
  useEffect(() => {
    // Si nous avons déjà l'entreprise depuis la navigation, ne pas la récupérer à nouveau
    if (location.state?.company) return;
    
    const fetchCompany = async () => {
      setLoading(true);
      setError(null);
      try {
        const companyData = await getCompanyById(id);
        if (companyData) {
          setCompany(companyData);
        } else {
          setError("Entreprise non trouvée");
          setTimeout(() => navigate("/admin/companies/"), 2000);
        }
      } catch (err) {
        console.error("Erreur lors de la récupération de l'entreprise:", err);
        setError("Impossible de charger les détails de l'entreprise");
        setTimeout(() => navigate("/admin/companies/"), 2000);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchCompany();
    } else {
      setError("ID d'entreprise non valide");
      setTimeout(() => navigate("/admin/companies/"), 2000);
    }
  }, [id, navigate, location.state]);

  // Récupérer le nombre de contrats actifs lorsque l'entreprise est chargée
  useEffect(() => {
    if (!company || !company.id) return;
    
    const fetchActiveContractsCount = async () => {
      try {
        const count = await countActiveCompanyContracts(company.id);
        setActiveContractsCount(count);
      } catch (error) {
        console.error('Erreur lors du comptage des contrats actifs:', error);
      }
    };
    
    fetchActiveContractsCount();
    
    // Mettre en place l'écoute en temps réel des changements de contrats
    const contractsSubscription = subscribeToCompanyContracts(company.id, () => {
      // Mettre à jour le compteur de contrats actifs lorsqu'un changement est détecté
      fetchActiveContractsCount();
    });
    
    // Nettoyer l'abonnement lorsque le composant est démonté
    return () => {
      if (contractsSubscription) {
        unsubscribeFromContracts(contractsSubscription);
      }
    };
  }, [company]);

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loading}>Chargement des détails de l'entreprise...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.error}>{error}</div>
        <div>Redirection vers la liste des entreprises...</div>
      </div>
    );
  }

  if (!company) {
    return null;
  }

  return (
    <>
      <div className={styles.shop}>


        <Link className={cn("button-white", styles.button)} to="/admin/companies">
          <Icon name="arrow-left" />
          Retour
        </Link>

        <div className={styles.background}>
          <img src="/images/content/bg-shop.jpg" alt="Background" />
        </div>

        <Card className={styles.card}>

          {/* Profile */}

          <div className={styles.profile}>
            <div className={styles.details}>
              <div className={styles.avatar}>
                <img src={company.logoUrl || '/images/content/company_logo.png'} alt="Avatar" />
              </div>
              <div className={styles.wrap}>
                <div className={cn("h4", styles.man)}>{company.name || 'Entreprise'}</div>
                <div className={styles.login}>{company.sector || 'Non spécifié'}</div>
              </div>
            </div>
            <div className={styles.statusContainer}>
              <div className={styles.statusGroup}>
                <div className={styles.login}>Type</div>
                <div className={cn(
                  styles.statusBadge,
                  'status-type',
                  'status-blue-dark'
                )}>
                  {company.type_of_company || 'Non spécifié'}
                </div>
              </div>
              <div className={styles.statusGroup}>
                <div className={styles.login}>Taille</div>
                <div className={cn(
                  styles.statusBadge,
                  'status-type',
                  'status-green-dark'
                )}>
                  {company.employee_count ? `${company.employee_count} employés` : 'Non spécifié'}
                </div>
              </div>

              <div className={styles.statusGroup}>
                <div className={styles.login}>Contrats actifs</div>
                <div className={cn(
                  styles.statusBadge,
                  'status-type',
                  (activeContractsCount > 0) ? 'status-green-dark' : 'status-gray-dark'
                )}>
                  {activeContractsCount} contrat(s)
                </div>
              </div>
            </div>
          </div>
          
          <div className={styles.control}>
            <div className={cn(styles.nav, styles.desktopNav)}>
              {navigation.map((x, index) => (
                <button
                  className={cn(styles.link, {
                    [styles.active]: index === activeIndex,
                  })}
                  onClick={() => setActiveIndex(index)}
                  key={index}
                >
                  {x}
                </button>
              ))}
            </div>
            
            <div className={styles.actionContainer}>
              {updatingAction ? (
                <div className={styles.loading}>Mise à jour en cours...</div>
              ) : (
                <Actions
                  className={styles.actions}
                  classActionsHead={styles.actionsHead}
                  items={[
                    {
                      title: 'Réinitialiser les identifiants',
                      action: handleSendCredentials
                    }
                  ]}
                  small
                />
              )}
            </div>

            {/* Mobile Navigation */}
            <div className={styles.mobileNav}>
              <Dropdown
                className={styles.dropdown}
                value={navigation[activeIndex]}
                setValue={(value) => {
                  const index = navigation.findIndex(x => x === value);
                  if (index !== -1) setActiveIndex(index);
                }}
                options={navigation}
              />
            </div>
          </div>

        </Card>

        {/* Pages */}
        <div className={styles.wrap}>
          {activeIndex === 0 && (
            <>
              <NameAndDescription company={company} />
            </>
          )}
          {activeIndex === 1 && (
            <>
              <Tutor company={company} />
            </>
          )}
          {activeIndex === 2 && (
            <>
              <ContractAndFiles companyId={company.id}/>
            </>
          )}
          {activeIndex === 3 && (
            <>
              <MemosAndNotes companyId={company.id}/>
            </>
          )}
          {activeIndex === 4 && (
            <>
              <ContractAndConvention companyId={company.id}/>
            </>
          )}
          
        </div>

      </div>
    </>
  );
};

export default CompanyDetail;
